import { Checkbox } from '@/components/nsw/Checkbox'
import SanitisedHTMLContainer from '@/components/SanitisedHTMLContainer'
import { ResponsiveTooltip } from '@/components/tooltip/Tooltip'
import { CustomViewErrorAlert } from '@/layouts/wp_custom_view'
import SearchBar from '@/legacy-ported/components/base/SearchBar'
import { TreeElement } from '@/legacy-ported/components/custom/treeUtils'
import FormControlLabel from '@mui/material/FormControlLabel'
import { ReactNode, useEffect, useMemo, useRef, useState } from 'react'

export interface SyllabusPickerProps {
	/**
	 * Flat list of syllabus elements
	 */
	rootElements: TreeElement[]

	/**
	 * Currently selected syllabus IDs
	 */
	selected: TreeElement['id'][]

	/**
	 * Syllabus IDs that are pending selection (checked but not yet moved)
	 */
	pending?: TreeElement['id'][]

	/**
	 * Callback fired with selected syllabus IDs
	 */
	onChange?: (_ids: TreeElement['id'][]) => void

	title?: string
	slotAfterTitle?: ReactNode
	className?: string

	/**
	 * Maximum number of syllabuses that can be selected
	 */
	maxSyllabuses?: number

	/**
	 * Error message to display when selection limit is exceeded
	 */
	maxSyllabusesErrorMessage?: string

	/**
	 * Maximum number of visible items before showing scrollbar (default: 10)
	 */
	maxVisibleItems?: number
}

export const SyllabusPicker = (props: SyllabusPickerProps) => {
	const {
		className,
		rootElements = [],
		selected,
		pending = [],
		onChange,
		title,
		slotAfterTitle,
		maxSyllabuses = 10,
		maxSyllabusesErrorMessage = 'Maximum 10 number of syllabuse reached.',
		maxVisibleItems = 10,
	} = props

	const [searchFilter, setSearchFilter] = useState('')
	const scrollableContainerRef = useRef<HTMLDivElement>(null)

	// Scroll to top when search filter changes
	useEffect(() => {
		if (scrollableContainerRef.current) {
			scrollableContainerRef.current.scrollTop = 0
		}
	}, [searchFilter])

	// Get all syllabuses sorted alphabetically
	const allSyllabuses = useMemo(() => {
		return rootElements.sort((a, b) => a.label.localeCompare(b.label))
	}, [rootElements])

	// Separate selected and unselected syllabuses with search filtering
	const { selectedSyllabuses, unselectedSyllabuses } = useMemo(() => {
		const allSylls = allSyllabuses
		const searchFilterLower = searchFilter.toLowerCase()

		// Separate all syllabuses into selected and unselected first
		// Note: pending items stay in unselected section but show as checked
		const allSelected = allSylls.filter((s) => selected.includes(s.id))
		const allUnselected = allSylls.filter((s) => !selected.includes(s.id))

		// Apply search filter ONLY to unselected items
		// Selected items should always be visible at top
		// Search through both label and seoKeywords fields
		const filteredUnselected = searchFilter
			? allUnselected.filter((s) => {
					const labelMatch = s.label
						.toLowerCase()
						.includes(searchFilterLower)
					const keywordsMatch =
						s.seoKeywords
							?.toLowerCase()
							.includes(searchFilterLower) || false
					return labelMatch || keywordsMatch
			  })
			: allUnselected

		return {
			selectedSyllabuses: allSelected.sort((a, b) =>
				a.label.localeCompare(b.label),
			),
			unselectedSyllabuses: filteredUnselected.sort((a, b) =>
				a.label.localeCompare(b.label),
			),
		}
	}, [allSyllabuses, selected, searchFilter])

	// Check if selection limit is exceeded
	const isLimitExceeded = selected.length > maxSyllabuses

	// Check if search has no results
	const hasNoSearchResults =
		searchFilter.trim() !== '' && unselectedSyllabuses.length === 0

	// Calculate total visible items to determine if scrollbar is needed
	const totalVisibleItems =
		selectedSyllabuses.length + unselectedSyllabuses.length
	const shouldShowScrollbar = totalVisibleItems > maxVisibleItems

	// Use rem units for better responsive design
	// Adjusted to show the correct number of items based on actual measurements
	// 4.5rem ≈ 72px per item (accounts for FormControlLabel + padding + margins)
	const containerStyle = shouldShowScrollbar
		? {
				maxHeight: `${maxVisibleItems * 3.6}rem`,
				overflowY: 'scroll' as const,
		  }
		: {}

	const handleSyllabusToggle = (syllabusId: string) => {
		if (onChange) {
			// Always allow selection/deselection - let the error message handle the warning
			onChange([syllabusId])
		}
	}

	return (
		<div className={className}>
			{!!title && (
				<div className="bold border-b-[3px] border-[#22272B] pb-3 mb-4">
					{title}
				</div>
			)}
			{isLimitExceeded ? (
				<CustomViewErrorAlert>
					{maxSyllabusesErrorMessage}
				</CustomViewErrorAlert>
			) : (
				slotAfterTitle
			)}

			{/* Search Box */}
			<div className="mb-2">
				<SearchBar
					value={searchFilter}
					placeholder="Search"
					onSearch={setSearchFilter}
				/>
			</div>

			{/* Scrollable Container for Syllabuses */}
			<div
				ref={scrollableContainerRef}
				className="rounded p-2 pl-0"
				style={containerStyle}
			>
				{/* Selected Syllabuses (at top) */}
				{selectedSyllabuses.length > 0 && (
					<div className="space-y-1 mb-2">
						{selectedSyllabuses.map((syllabus) => (
							<div key={syllabus.id} className="space-y-1">
								<FormControlLabel
									className="mx-0 flex w-full items-center"
									sx={{
										'& .MuiFormControlLabel-label': {
											width: '100%',
										},
									}}
									data-syllabus-id={syllabus.id}
									control={
										<Checkbox
											checked={true}
											onChange={() =>
												handleSyllabusToggle(
													syllabus.id,
												)
											}
											disabled={syllabus.disabled}
										/>
									}
									label={
										<div className="flex justify-between items-center w-full">
											<span className="break-words leading-tight mr-4">
												{syllabus.label}
											</span>
											{syllabus.moreInfo && (
												<div className="w-[24px] flex-shrink-0">
													<ResponsiveTooltip
														iconSizeInTooltip={24}
														text={
															<SanitisedHTMLContainer>
																{
																	syllabus.moreInfo
																}
															</SanitisedHTMLContainer>
														}
														placement="top"
													/>
												</div>
											)}
										</div>
									}
								/>
								{/* Related Life Skills Links */}
								{syllabus.hasRelatedLifeSkills &&
									syllabus.relatedLifeSkillsInfo?.map(
										(lifeSkill) => {
											// Check if this related life skills syllabus is not already selected
											const isLifeSkillSelected =
												selected.includes(
													lifeSkill.codename,
												)

											if (!isLifeSkillSelected) {
												return (
													<div
														key={lifeSkill.codename}
														className="ml-12 !-mt-2 lg:!-mt-4"
													>
														<button
															onClick={() =>
																handleSyllabusToggle(
																	lifeSkill.codename,
																)
															}
															className="text-blue-600 hover:text-blue-800 underline text-sm text-left break-words"
														>
															Include{' '}
															{lifeSkill.title}
														</button>
													</div>
												)
											}
											return null
										},
									)}
							</div>
						))}
					</div>
				)}

				{/* Unselected Syllabuses */}
				<div className="space-y-1">
					{hasNoSearchResults ? (
						<div className="text-center p-4 bold">
							We didn&apos;t find any results.{' '}
							<button
								onClick={() => setSearchFilter('')}
								className="text-[#002664] hover:text-[#002664] underline"
							>
								Clear all
							</button>
						</div>
					) : (
						unselectedSyllabuses.map((syllabus) => (
							<FormControlLabel
								key={syllabus.id}
								className="mx-0 flex w-full items-center"
								sx={{
									'& .MuiFormControlLabel-label': {
										width: '100%',
									},
								}}
								data-syllabus-id={syllabus.id}
								control={
									<Checkbox
										checked={pending.includes(syllabus.id)}
										onChange={() =>
											handleSyllabusToggle(syllabus.id)
										}
										disabled={syllabus.disabled}
									/>
								}
								label={
									<div className="flex justify-between items-center w-full">
										<span className="break-words leading-tight mr-4">
											{syllabus.label}
										</span>
										{syllabus.moreInfo && (
											<div className="w-[24px] flex-shrink-0">
												<ResponsiveTooltip
													iconSizeInTooltip={24}
													text={
														<SanitisedHTMLContainer>
															{syllabus.moreInfo}
														</SanitisedHTMLContainer>
													}
													placement="top"
												/>
											</div>
										)}
									</div>
								}
							/>
						))
					)}
				</div>
			</div>
		</div>
	)
}

export default SyllabusPicker
